/**
 * The property you were working on.
 *
 * E07-S01: post-login lands on the last or current property's Attention *where
 * context exists*. S10B §3 puts it plainly — "Property operation is primary;
 * Portfolio is secondary" — and until Sprint 11 every sign-in landed on the
 * portfolio, so a manager who lives in one building started each day one
 * navigation away from their work.
 *
 * "Where context exists" is the whole qualifier. A first sign-in has no
 * context and must not invent one; landing somewhere arbitrary is worse than
 * landing on the list.
 *
 * IT FORGETS ON REFUSAL, which is the lesson from the stored organization id.
 * That was persisted forever, including after the tenant behind it was deleted
 * by a test cleanup, and every request afterwards carried an id the API would
 * not accept — a 403 on every screen with no way out but a browser console.
 * A remembered property that can no longer be reached is worth nothing
 * whatever the reason (access revoked, property deleted, an id from another
 * environment), so the first refusal drops it. Wrong once, then correct;
 * rather than wrong forever, silently.
 */

const KEY = 'strata.lastProperty';

/** Kept out of the way of a browser that refuses storage entirely. */
function safely<T>(work: () => T, fallback: T): T {
  try {
    return work();
  } catch {
    // Private mode, disabled site data, a sandboxed frame. Losing the
    // convenience is fine; throwing on the sign-in path is not.
    return fallback;
  }
}

export function rememberProperty(id: string): void {
  if (!id) return;
  safely(() => localStorage.setItem(KEY, id), undefined);
}

export function lastProperty(): string | null {
  return safely(() => localStorage.getItem(KEY), null);
}

export function forgetProperty(): void {
  safely(() => localStorage.removeItem(KEY), undefined);
}

/**
 * Where a fresh sign-in should go.
 *
 * `next` wins whenever the guard sent one: that is the screen the person was
 * actually trying to reach, and overriding it with a remembered property would
 * reintroduce the bug the guard exists to fix.
 */
export function landingAfterSignIn(next: string): string {
  if (next !== '/') return next;
  const id = lastProperty();
  return id ? `/p/${id}/attention` : '/';
}
